import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { ColumnDef } from "@tanstack/react-table";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { EmptyTableMessage } from "@/core/components/empty-table-message";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { SearchInput } from "@/core/components/search-input";
import { SimpleTable } from "@/core/components/simple-table";
import { SwitchWithLabel } from "@/core/components/switch-with-label";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import {
  TimeRangePicker,
  TimeRange,
  TimeRangePreset,
} from "@/core/components/ui/time-range-picker";
import { useBusiness } from "@/core/business";
import { downloadCSV } from "@/core/utils/download-csv";
import { cn } from "@/core/utils/utils";
import { ROLE_TO_LABEL } from "@/user/constants";
import { DetailedUser, DetailedUserWithStats } from "@/user/user.d";
// import { ActionsCol } from "./cols/actions-col";
import { ClosedReferralsCol } from "./cols/closed-referrals-col";
import { EmailCol } from "./cols/email-col";
import { NameCol } from "./cols/name-col";
import { ReferralCodeCol } from "./cols/referral-code-col";
import { RoleCol } from "./cols/role-col";
import { SignupsCol } from "./cols/signups-col";
import { TotalReferralsCol } from "./cols/total-referrals-col";

const CSV_HEADERS = [
  [
    "Name",
    "Email",
    "Role",
    "Referral Code",
    "Signups",
    "Total Referrals",
    "Closed Referrals",
  ],
];

interface UsersTableProps {
  data: DetailedUser[] | DetailedUserWithStats[];
  className?: string;
  tableClassName?: string;
  isLoading?: boolean;
  isLoadingStats?: boolean;
  onRowClick?: (row: DetailedUser | DetailedUserWithStats) => void;
  selectedUserId?: string;
  showReporting?: boolean;
  setShowReporting?: (value: boolean) => void;
  timeRange?: TimeRange | null;
  setTimeRange?: Dispatch<SetStateAction<TimeRange | null>>;
  presets?: TimeRangePreset[];
}

const reportingColumns = [
  SignupsCol(),
  TotalReferralsCol(),
  ClosedReferralsCol(),
];

export const UsersTable = ({
  data,
  className,
  isLoading,
  isLoadingStats,
  onRowClick,
  selectedUserId,
  tableClassName,
  showReporting = false,
  setShowReporting,
  timeRange,
  setTimeRange,
  presets,
}: UsersTableProps) => {
  const { isSpiffOnly } = useBusiness();
  const [searchValue, setSearchValue] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  const baseColumns = useMemo(
    () =>
      [
        NameCol(),
        EmailCol(),
        RoleCol(),
        !isSpiffOnly && ReferralCodeCol(),
      ].filter(Boolean) as ColumnDef<DetailedUser | DetailedUserWithStats>[],
    [isSpiffOnly],
  );

  const columns = useMemo(
    () =>
      showReporting && !isSpiffOnly
        ? [...baseColumns, ...reportingColumns]
        : baseColumns,
    [showReporting, isSpiffOnly],
  );

  const handleDownloadCSV = async () => {
    if (!showReporting) return;

    setIsDownloading(true);
    try {
      const csvData = [CSV_HEADERS[0]];
      data.forEach((row: DetailedUserWithStats | DetailedUser) => {
        const stats = "stats" in row ? row.stats : null;
        csvData.push([
          `${row.first_name} ${row.last_name}`,
          row.email,
          ROLE_TO_LABEL[row.role] || "",
          row.referral_code ? `Referral${row.referral_code}` : "",
          (stats?.signup_count || 0).toString(),
          (stats?.total_referral_count || 0).toString(),
          (stats?.closed_referral_count || 0).toString(),
        ]);
      });
      const filename = timeRange
        ? `users-report-${timeRange.from.toISOString().split("T")[0]}-to-${timeRange.to.toISOString().split("T")[0]}.csv`
        : "users-report.csv";
      downloadCSV(csvData, filename);
    } catch (error) {
      console.error("Error generating CSV:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (data.length === 0 && !isLoading) {
    return <EmptyTableMessage />;
  }

  return (
    <Stack
      gap="sm"
      className={cn("flex h-full min-h-0 flex-col", className)}
      fullWidth
    >
      <Stack
        direction="row"
        justify="between"
        align="center"
        className="shrink-0 pr-5"
        fullWidth
      >
        <Stack direction="row" align="center" gap="md">
          <SearchInput
            placeholder="Search Users"
            value={searchValue}
            onChange={setSearchValue}
          />
          {setShowReporting && !isSpiffOnly && (
            <SwitchWithLabel
              label="Show reporting"
              checked={showReporting}
              onChange={setShowReporting}
            />
          )}
        </Stack>
        {showReporting && (
          <Stack direction="row" align="center" gap="xs">
            {isLoadingStats && <LoadingSpinner size="sm" color="secondary" />}
            <TimeRangePicker
              value={timeRange}
              onChange={setTimeRange}
              placeholder="Select date range"
              presets={presets}
            />
            <Button
              variant="text"
              size="sm"
              onClick={handleDownloadCSV}
              disabled={isLoading || isLoadingStats}
              loading={isDownloading}
              leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
            >
              Download report
            </Button>
          </Stack>
        )}
      </Stack>
      <SimpleTable
        data={data}
        columns={columns}
        className="min-h-0 w-full flex-1 overflow-hidden"
        isLoading={isLoading}
        onRowClick={onRowClick}
        selectedRowId={selectedUserId}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        tableClassName={cn(
          showReporting ? "min-w-[1200px] mb-2" : tableClassName,
          tableClassName,
        )}
      />
    </Stack>
  );
};
